import React from 'react';
import { H5 } from '../../../../AbstractElements';
import { FormGroup, Input, Label, FormFeedback, Row, Col } from 'reactstrap';
import PropTypes from 'prop-types';

const ibanLengths = {
    AL: 28, AD: 24, AT: 20, AZ: 28, BH: 22, BE: 16, BA: 20, BR: 29, BG: 22,
    CR: 22, HR: 21, CY: 28, CZ: 24, DK: 18, DO: 28, EE: 20, FO: 18, FI: 18,
    FR: 27, GE: 22, DE: 22, GI: 23, GR: 27, GL: 18, GT: 28, HU: 28, IS: 26,
    IE: 22, IL: 23, IT: 27, JO: 30, KZ: 20, KW: 30, LV: 21, LB: 28, LI: 21,
    LT: 20, LU: 20, MT: 31, MR: 27, MU: 30, MD: 24, MC: 27, ME: 22, NL: 18,
    MK: 19, NO: 15, PK: 24, PS: 29, PL: 28, PT: 25, QA: 29, RO: 24, SM: 27,
    SA: 24, RS: 22, SK: 24, SI: 19, ES: 24, SE: 24, CH: 21, TN: 24, TR: 26,
    AE: 23, GB: 22, VG: 24,
};

const ibanRegex = /^[A-Z]{2}[0-9]{2}[A-Z0-9]+$/;

const ContactInfoForm = ({ formData, errors, handleInputChange, setFormData, fieldPermissions = [], canEdit = true }) => {
    // Helper function to get field access properties
    const getFieldAccess = (fieldName) => {
        // If canEdit is false, all fields should be disabled regardless of permissions
        if (!canEdit) {
            return {
                editable: false,
                required: false,
            };
        }
        
        const permission = fieldPermissions.find(p => p.fieldName === fieldName);
        // Adjust default editability if needed (e.g., default to false/ReadOnly)
        return {
            editable: permission ? permission.access === 'Editable' : true,
            required: permission ? permission.required : false,
        };
    };

    // Map form fields to API field names and get access rules
    const companyRepAccess = getFieldAccess('customers.companyrepresentative');
    const emailDescAccess = getFieldAccess('customers.email.emaildescription');
    const emailAccess = getFieldAccess('customers.email.email');
    const phoneDescAccess = getFieldAccess('customers.phones.phonedescription');
    const phoneAccess = getFieldAccess('customers.phones.phone');
    const ibanDescAccess = getFieldAccess('customers.ibans.ibandescription');
    const ibanAccess = getFieldAccess('customers.ibans.iban');
    const middlemanAccess = getFieldAccess('customers.middleman');
    const middlemanIbanAccess = getFieldAccess('customers.middlemaniban');
    const middlemanRateAccess = getFieldAccess('customers.middlemancontractprice');
    const isMiddlemanContractBasedAccess = getFieldAccess('customers.ismiddlemancontractbased');
    const middlemanContractPriceAccess = getFieldAccess('customers.middlemancontractprice');

    return (
        <>
            <H5>İletişim Bilgileri</H5>
            <div style={{ height: "20px" }}></div>

            {formData.customerType === 'Kurumsal' && (
                <>
                    <Row>
                        <Col md={12}>
                            <FormGroup>
                                <Label for="companyRepresentative">
                                    Firma Yetkilisi {companyRepAccess.required && <span className="text-danger">*</span>}
                                </Label>
                                <Input
                                    type="text"
                                    id="companyRepresentative"
                                    name="companyRepresentative"
                                    value={formData.companyRepresentative || ''}
                                    disabled={!companyRepAccess.editable}
                                    onChange={handleInputChange}
                                    invalid={!!errors.companyRepresentative}
                                />
                                <FormFeedback>{errors.companyRepresentative}</FormFeedback>
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={6}>
                            <FormGroup>
                                <Label for="emailDescription">
                                    E-Mail Açıklaması {emailDescAccess.required && <span className="text-danger">*</span>}
                                </Label>
                                <Input
                                    type="text"
                                    id="emailDescription"
                                    name="emailDescription"
                                    value={formData.emailDescription || ''}
                                    disabled={!emailDescAccess.editable}
                                    onChange={handleInputChange}
                                    placeholder="Örn: İş E-postası"
                                    invalid={!!errors.emailDescription}
                                />
                                <FormFeedback>{errors.emailDescription}</FormFeedback>
                            </FormGroup>
                            <FormGroup>
                                <Label for="email">
                                    E-Mail {emailAccess.required && <span className="text-danger">*</span>}
                                </Label>
                                <Input
                                    type="email"
                                    id="email"
                                    name="email"
                                    value={formData.email || ''}
                                    disabled={!emailAccess.editable}
                                    onChange={handleInputChange}
                                    invalid={!!errors.email}
                                />
                                <FormFeedback>{errors.email}</FormFeedback>
                            </FormGroup>
                        </Col>
                        <Col md={6}>
                            <FormGroup>
                                <Label for="emailDescription2">
                                    E-Mail 2 Açıklaması {emailDescAccess.required && <span className="text-danger">*</span>}
                                </Label>
                                <Input
                                    type="text"
                                    id="emailDescription2"
                                    name="emailDescription2"
                                    value={formData.emailDescription2 || ''}
                                    disabled={!emailDescAccess.editable}
                                    onChange={handleInputChange}
                                    placeholder="Örn: Kişisel E-posta"
                                    invalid={!!errors.emailDescription2}
                                />
                                <FormFeedback>{errors.emailDescription2}</FormFeedback>
                            </FormGroup>
                            <FormGroup>
                                <Label for="email2">
                                    E-Mail 2 {emailAccess.required && <span className="text-danger">*</span>}
                                </Label>
                                <Input
                                    type="email"
                                    id="email2"
                                    name="email2"
                                    value={formData.email2 || ''}
                                    disabled={!emailAccess.editable}
                                    onChange={handleInputChange}
                                    invalid={!!errors.email2}
                                />
                                <FormFeedback>{errors.email2}</FormFeedback>
                            </FormGroup>
                        </Col>
                    </Row>
                </>
            )}

            <Row>
                <Col md={6}>
                    <FormGroup>
                        <Label for="phoneDescription">
                            Telefon Açıklaması {phoneDescAccess.required && <span className="text-danger">*</span>}
                        </Label>
                        <Input
                            type="text"
                            id="phoneDescription"
                            name="phoneDescription"
                            value={formData.phoneDescription || ''}
                            disabled={!phoneDescAccess.editable}
                            onChange={handleInputChange}
                            placeholder="Örn: Ana Telefon"
                            invalid={!!errors.phoneDescription}
                        />
                        <FormFeedback>{errors.phoneDescription}</FormFeedback>
                    </FormGroup>
                    <FormGroup>
                        <Label for="phoneNumber">
                            Telefon Numarası {phoneAccess.required && <span className="text-danger">*</span>}
                        </Label>
                        <Input
                            type="text"
                            id="phoneNumber"
                            name="phoneNumber"
                            value={formData.phoneNumber || ''}
                            disabled={!phoneAccess.editable}
                            onChange={(e) => {
                                let value = e.target.value.replace(/[^0-9]/g, '').slice(0, 11);
                                if (value && !value.startsWith('05')) {
                                    if (value.startsWith('5')) {
                                        value = '0' + value;
                                    } else {
                                        value = '';
                                    }
                                }
                                handleInputChange({
                                    target: {
                                        name: 'phoneNumber',
                                        value
                                    }
                                });
                            }}
                            placeholder="05XX XXX XX XX"
                            maxLength={11}
                            invalid={!!errors.phoneNumber || (formData.phoneNumber && (!formData.phoneNumber.startsWith('05') || formData.phoneNumber.length !== 11))}
                        />
                        <FormFeedback>
                            {errors.phoneNumber || 'Telefon numarası 05 ile başlamalı ve 11 haneli olmalıdır'}
                        </FormFeedback>
                    </FormGroup>
                </Col>
                <Col md={6}>
                    <FormGroup>
                        <Label for="phoneDescription2">
                            Telefon 2 Açıklaması {phoneDescAccess.required && <span className="text-danger">*</span>}
                        </Label>
                        <Input
                            type="text"
                            id="phoneDescription2"
                            name="phoneDescription2"
                            value={formData.phoneDescription2 || ''}
                            disabled={!phoneDescAccess.editable}
                            onChange={handleInputChange}
                            placeholder="Örn: Yedek Telefon"
                            invalid={!!errors.phoneDescription2}
                        />
                        <FormFeedback>{errors.phoneDescription2}</FormFeedback>
                    </FormGroup>
                    <FormGroup>
                        <Label for="phoneNumber2">
                            Telefon Numarası 2 {phoneAccess.required && <span className="text-danger">*</span>}
                        </Label>
                        <Input
                            type="text"
                            id="phoneNumber2"
                            name="phoneNumber2"
                            value={formData.phoneNumber2 || ''}
                            disabled={!phoneAccess.editable}
                            onChange={(e) => {
                                let value = e.target.value.replace(/[^0-9]/g, '').slice(0, 11);
                                if (value && !value.startsWith('05')) {
                                    if (value.startsWith('5')) {
                                        value = '0' + value;
                                    } else {
                                        value = '';
                                    }
                                }
                                handleInputChange({
                                    target: {
                                        name: 'phoneNumber2',
                                        value
                                    }
                                });
                            }}
                            placeholder="05XX XXX XX XX"
                            maxLength={11}
                            invalid={!!errors.phoneNumber2 || (formData.phoneNumber2 && formData.phoneNumber2.length > 0 && (!formData.phoneNumber2.startsWith('05') || formData.phoneNumber2.length !== 11))}
                        />
                        <FormFeedback>
                            {errors.phoneNumber2 || 'Telefon numarası 05 ile başlamalı ve 11 haneli olmalıdır'}
                        </FormFeedback>
                    </FormGroup>
                </Col>
            </Row>

            <Row>
                <Col md={6}>
                    <FormGroup>
                        <Label for="ibanDescription">
                            İban Açıklaması {ibanDescAccess.required && <span className="text-danger">*</span>}
                        </Label>
                        <Input
                            type="text"
                            id="ibanDescription"
                            name="ibanDescription"
                            value={formData.ibanDescription || ''}
                            disabled={!ibanDescAccess.editable}
                            onChange={handleInputChange}
                            placeholder="Örn: Ana Hesap"
                            invalid={!!errors.ibanDescription}
                        />
                        <FormFeedback>{errors.ibanDescription}</FormFeedback>
                    </FormGroup>
                    <FormGroup>
                        <Label for="iban">
                            İban {ibanAccess.required && <span className="text-danger">*</span>}
                        </Label>
                        <Input
                            type="text"
                            id="iban"
                            name="iban"
                            value={formData.iban || ''}
                            disabled={!ibanAccess.editable}
                            onChange={(e) => {
                                let value = e.target.value.toUpperCase().replace(/\s/g, '');
                                handleInputChange({
                                    target: {
                                        name: 'iban',
                                        value
                                    }
                                });
                            }}
                            placeholder="TR00 0000 0000 0000 0000 0000 00"
                            invalid={!!errors.iban || (formData.iban && (!ibanRegex.test(formData.iban) || (ibanLengths[formData.iban.slice(0, 2)] && formData.iban.length !== ibanLengths[formData.iban.slice(0, 2)])))}
                        />
                        <FormFeedback>
                            {errors.iban || (formData.iban && !ibanRegex.test(formData.iban) ? 'Geçerli bir IBAN formatı giriniz' : formData.iban && ibanLengths[formData.iban.slice(0, 2)] ? `IBAN uzunluğu ${ibanLengths[formData.iban.slice(0, 2)]} karakter olmalıdır (${formData.iban.slice(0, 2)})` : 'Geçerli bir ülke kodu içeren IBAN giriniz')}
                        </FormFeedback>
                    </FormGroup>
                </Col>
                <Col md={6}>
                    <FormGroup>
                        <Label for="ibanDescription2">
                            İban 2 Açıklaması {ibanDescAccess.required && <span className="text-danger">*</span>}
                        </Label>
                        <Input
                            type="text"
                            id="ibanDescription2"
                            name="ibanDescription2"
                            value={formData.ibanDescription2 || ''}
                            disabled={!ibanDescAccess.editable}
                            onChange={handleInputChange}
                            placeholder="Örn: Yedek Hesap"
                            invalid={!!errors.ibanDescription2}
                        />
                        <FormFeedback>{errors.ibanDescription2}</FormFeedback>
                    </FormGroup>
                    <FormGroup>
                        <Label for="iban2">
                            İban 2 {ibanAccess.required && <span className="text-danger">*</span>}
                        </Label>
                        <Input
                            type="text"
                            id="iban2"
                            name="iban2"
                            value={formData.iban2 || ''}
                            disabled={!ibanAccess.editable}
                            onChange={(e) => {
                                let value = e.target.value.toUpperCase().replace(/\s/g, '');
                                handleInputChange({
                                    target: {
                                        name: 'iban2',
                                        value
                                    }
                                });
                            }}
                            placeholder="TR00 0000 0000 0000 0000 0000 00"
                            invalid={!!errors.iban2 || (formData.iban2 && formData.iban2.length > 0 && (!ibanRegex.test(formData.iban2) || (ibanLengths[formData.iban2.slice(0, 2)] && formData.iban2.length !== ibanLengths[formData.iban2.slice(0, 2)])))}
                        />
                        <FormFeedback>
                            {errors.iban2 || (formData.iban2 && !ibanRegex.test(formData.iban2) ? 'Geçerli bir IBAN formatı giriniz' : formData.iban2 && ibanLengths[formData.iban2.slice(0, 2)] ? `IBAN uzunluğu ${ibanLengths[formData.iban2.slice(0, 2)]} karakter olmalıdır (${formData.iban2.slice(0, 2)})` : 'Geçerli bir ülke kodu içeren IBAN giriniz')}
                        </FormFeedback>
                    </FormGroup>
                </Col>
            </Row>

            <Row>
                <Col md={6}>
                    <FormGroup>
                        <Label for="middleman">
                            Aracı {middlemanAccess.required && <span className="text-danger">*</span>}
                        </Label>
                        <Input
                            type="text"
                            id="middleman"
                            name="middleman"
                            value={formData.middleman || ''}
                            disabled={!middlemanAccess.editable}
                            onChange={(e) => {
                                const value = e.target.value
                                    .replace(/[^a-zA-ZğüşıöçĞÜŞİÖÇ\s]/g, '')
                                    .replace(/^\s+/, '')
                                    .replace(/\s{2,}/g, ' ')
                                    .split(' ')
                                    .map(word => word ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase() : '')
                                    .join(' ');

                                // İsmi formData'ya ekle
                                handleInputChange({
                                    target: {
                                        name: 'middleman',
                                        value
                                    }
                                });
                            }}
                            placeholder="Aracı Ad Soyad"
                            invalid={!!errors.middleman || (formData.middleman && formData.middleman.length > 0 && !/^[A-ZĞÜŞİÖÇ][a-zğüşıöç]+(?: [A-ZĞÜŞİÖÇ][a-zğüşıöç]+)+$/.test(formData.middleman.trim()))}
                        />
                        <FormFeedback>{errors.middleman || 'Ad ve soyad büyük harfle başlamalı ve en az iki kelimeden oluşmalıdır'}</FormFeedback>
                    </FormGroup>
                </Col>
                <Col md={6}>
                    <FormGroup>
                        <Label for="middlemanIban">
                            Aracı IBAN {middlemanIbanAccess.required && <span className="text-danger">*</span>}
                        </Label>
                        <Input
                            type="text"
                            id="middlemanIban"
                            name="middlemanIban"
                            value={formData.middlemanIban || ''}
                            disabled={!middlemanIbanAccess.editable}
                            onChange={(e) => {
                                // IBAN formatı: alfanumerik, boşluk olmadan, maksimum 34 karakter
                                const value = e.target.value.replace(/[^A-Z0-9]/gi, '').toUpperCase();
                                handleInputChange({
                                    target: {
                                        name: 'middlemanIban',
                                        value
                                    }
                                });
                            }}
                            maxLength={34}
                            placeholder="Örn: TR123456789012345678901234"
                            invalid={!!errors.middlemanIban}
                        />
                        <FormFeedback>{errors.middlemanIban}</FormFeedback>
                    </FormGroup>
                </Col>
            </Row>

            <Row>
                <Col md={3}>
                    <FormGroup className="mb-2">
                        <Label>
                            Aracı Ödeme Türü {isMiddlemanContractBasedAccess.required && <span className="text-danger">*</span>}
                        </Label>
                        <div className="d-flex mt-1">
                            <div className="radio radio-primary me-3 d-flex align-items-center">
                                <Input
                                    id="customerIsMiddlemanContractBasedTrue"
                                    type="radio"
                                    name="customerIsMiddlemanContractBased"
                                    value="true"
                                    checked={formData.customerIsMiddlemanContractBased === true || formData.customerIsMiddlemanContractBased === "true"}
                                    onChange={() => handleInputChange({ target: { name: 'customerIsMiddlemanContractBased', value: true } })}
                                    disabled={!isMiddlemanContractBasedAccess.editable}
                                />
                                <Label for="customerIsMiddlemanContractBasedTrue" className="ms-1 mb-0">
                                    Oran Bazlı
                                </Label>
                            </div>
                            <div className="radio radio-primary d-flex align-items-center">
                                <Input
                                    id="customerIsMiddlemanContractBasedFalse"
                                    type="radio"
                                    name="customerIsMiddlemanContractBased"
                                    value="false"
                                    checked={formData.customerIsMiddlemanContractBased === false || formData.customerIsMiddlemanContractBased === "false"}
                                    onChange={() => handleInputChange({ target: { name: 'customerIsMiddlemanContractBased', value: false } })}
                                    disabled={!isMiddlemanContractBasedAccess.editable}
                                />
                                <Label for="customerIsMiddlemanContractBasedFalse" className="ms-1 mb-0">
                                    Fiyat Bazlı
                                </Label>
                            </div>
                        </div>
                        {errors.customerIsMiddlemanContractBased && <FormFeedback style={{ display: 'block' }}>{errors.customerIsMiddlemanContractBased}</FormFeedback>}
                    </FormGroup>
                </Col>
                <Col md={3}>
                    {(formData.customerIsMiddlemanContractBased === true || formData.customerIsMiddlemanContractBased === "true") ? (
                        <FormGroup>
                            <Label for="customerMiddlemanContractRate">
                            Aracı Sözleşme Oranı (%) {middlemanRateAccess.required && <span className="text-danger">*</span>}
                        </Label>
                        <Input
                            type="text"
                                id="customerMiddlemanContractRate"
                                name="customerMiddlemanContractRate"
                                value={formData.customerMiddlemanContractRate || ''}
                            disabled={!middlemanRateAccess.editable}
                            onChange={(e) => {
                                let value = e.target.value;
                                // Sadece sayısal değerlere izin ver
                                if (value && !/^\d*$/.test(value)) {
                                    value = value.replace(/[^\d]/g, '');
                                }
                                // 0-100 aralığında kontrol
                                if (value && parseInt(value) > 100) {
                                    value = '100';
                                }
                                handleInputChange({
                                    target: {
                                            name: 'customerMiddlemanContractRate',
                                        value: value || null
                                    }
                                });
                            }}
                            placeholder="0-100"
                                invalid={!!errors.customerMiddlemanContractRate}
                        />
                            <FormFeedback>{errors.customerMiddlemanContractRate}</FormFeedback>
                        </FormGroup>
                    ) : (
                        <FormGroup>
                            <Label for="customerMiddlemanContractPrice">
                                Aracı Sözleşme Fiyatı (₺) {middlemanContractPriceAccess.required && <span className="text-danger">*</span>}
                            </Label>
                            <Input
                                type="text"
                                id="customerMiddlemanContractPrice"
                                name="customerMiddlemanContractPrice"
                                value={formData.customerMiddlemanContractPrice || ''}
                                disabled={!middlemanContractPriceAccess.editable}
                                onChange={(e) => {
                                    let value = e.target.value;
                                    // Sadece sayısal değerlere izin ver
                                    if (value && !/^\d*$/.test(value)) {
                                        value = value.replace(/[^\d]/g, '');
                                    }
                                    handleInputChange({
                                        target: {
                                            name: 'customerMiddlemanContractPrice',
                                            value: value || null
                                        }
                                    });
                                }}
                                placeholder="Örn: 1500"
                                invalid={!!errors.customerMiddlemanContractPrice}
                            />
                            <FormFeedback>{errors.customerMiddlemanContractPrice}</FormFeedback>
                    </FormGroup>
                    )}
                </Col>
            </Row>
        </>
    );
};

// Add PropTypes for the new prop and existing ones if missing
ContactInfoForm.propTypes = {
    formData: PropTypes.object.isRequired,
    errors: PropTypes.object.isRequired,
    handleInputChange: PropTypes.func.isRequired,
    setFormData: PropTypes.func.isRequired,
    fieldPermissions: PropTypes.array,
    canEdit: PropTypes.bool,
};

export default ContactInfoForm;
